import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { computeBannerState, deriveUsageState } from "../lib/usage";

const DISMISS_PREFIX = "zt_usage_banner_dismissed_";
const REFRESH_DEBOUNCE_MS = 5000;
const MIN_REFRESH_INTERVAL_MS = 30_000;

const UsageContext = createContext(null);

const readDismissed = (severity) => {
  if (!severity) return false;
  try {
    return sessionStorage.getItem(DISMISS_PREFIX + severity) === "true";
  } catch {
    return false;
  }
};

const writeDismissed = (severity) => {
  if (!severity) return;
  try {
    sessionStorage.setItem(DISMISS_PREFIX + severity, "true");
  } catch {
    /* ignore */
  }
};

export const UsageProvider = ({ children }) => {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dismissedVersion, setDismissedVersion] = useState(0);

  const apiBase = useMemo(
    () => (process.env.REACT_APP_BACKEND_URL || "http://localhost:5050").replace(/\/$/, ""),
    []
  );

  const inFlightRef = useRef(null);
  const lastFetchAtRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const fetchUsage = useCallback(async ({ force = false } = {}) => {
    if (inFlightRef.current) return inFlightRef.current;
    if (
      !force &&
      lastFetchAtRef.current > 0 &&
      Date.now() - lastFetchAtRef.current < MIN_REFRESH_INTERVAL_MS
    ) {
      return Promise.resolve();
    }
    const ctrl = new AbortController();
    const promise = (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiBase}/api/subscription/info`, {
          method: "GET",
          credentials: "include",
          signal: ctrl.signal,
        });
        const json = await res.json();
        if (!res.ok || !json?.success) {
          throw new Error(json?.error || "Failed to load usage data.");
        }
        const record = Array.isArray(json?.data) ? json.data[0] : null;
        setRaw(record || null);
        lastFetchAtRef.current = Date.now();
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message || "Failed to load usage data.");
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = promise;
    promise.cancel = () => ctrl.abort();
    return promise;
  }, [apiBase]);

  const refresh = useCallback(
    (opts = {}) => fetchUsage(opts),
    [fetchUsage]
  );

  const refreshDebounced = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      fetchUsage({ force: true });
    }, REFRESH_DEBOUNCE_MS);
  }, [fetchUsage]);

  useEffect(() => {
    fetchUsage();
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (inFlightRef.current?.cancel) inFlightRef.current.cancel();
    };
  }, [fetchUsage]);

  const usage = useMemo(() => deriveUsageState(raw), [raw]);
  const bannerState = useMemo(() => computeBannerState(usage), [usage]);

  const bannerDismissed = useMemo(() => {
    // Reading sessionStorage on render; dismissedVersion forces re-eval after dismiss()
    void dismissedVersion;
    return bannerState ? readDismissed(bannerState.severity) : false;
  }, [bannerState, dismissedVersion]);

  const dismissBanner = useCallback(() => {
    if (!bannerState) return;
    writeDismissed(bannerState.severity);
    setDismissedVersion((v) => v + 1);
  }, [bannerState]);

  const value = useMemo(
    () => ({
      raw,
      usage,
      loading,
      error,
      bannerState,
      bannerDismissed,
      refresh,
      refreshDebounced,
      dismissBanner,
    }),
    [
      raw,
      usage,
      loading,
      error,
      bannerState,
      bannerDismissed,
      refresh,
      refreshDebounced,
      dismissBanner,
    ]
  );

  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
};

export const useUsage = () => {
  const ctx = useContext(UsageContext);
  if (!ctx) {
    throw new Error("useUsage must be used within a <UsageProvider>");
  }
  return ctx;
};
